import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { TeamTimeEntryData } from "@/components/team/TeamTimeEntryForm";

export function useTeamTimeEntries(projectId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: entries = [], isLoading: isLoadingEntries } = useQuery({
    queryKey: ['team-time-entries', projectId],
    queryFn: async () => {
      // First get the time entries
      const { data: timeEntries, error } = await supabase
        .from('team_time_entries')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!timeEntries || timeEntries.length === 0) return [];

      // Get unique user IDs
      const userIds = [...new Set(timeEntries.map(entry => entry.user_id))];
      
      // Get user profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      // Map profiles to entries
      const profilesMap = new Map(profiles?.map(p => [p.id, p]) || []);
      
      return timeEntries.map(entry => {
        const profile = profilesMap.get(entry.user_id);
        return {
          ...entry,
          user_name: profile?.full_name || profile?.email || 'Unknown User'
        };
      });
    }
  });

  const addEntryMutation = useMutation({
    mutationFn: async (entry: TeamTimeEntryData) => {
      console.log('Adding team time entry:', entry);
      
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) {
        console.error('User not authenticated');
        throw new Error('Not authenticated');
      }

      console.log('Authenticated user:', user.user.id);

      const { data, error } = await supabase
        .from('team_time_entries')
        .insert({
          ...entry,
          user_id: user.user.id,
        })
        .select()
        .single();

      if (error) {
        console.error('Database error:', error);
        throw error;
      }
      
      console.log('Time entry created:', data);
      return data;
    },
    onSuccess: () => {
      console.log('Invalidating queries for project:', projectId);
      queryClient.invalidateQueries({ queryKey: ['team-time-entries', projectId] });
    }
  });

  const deleteEntryMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('team_time_entries')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-time-entries', projectId] });
    }
  });

  const handleAddEntry = async (entry: TeamTimeEntryData) => {
    console.log('handleAddEntry called with:', entry);
    try {
      await addEntryMutation.mutateAsync(entry);
      console.log('Successfully added time entry');
      toast({
        title: "Time Entry Added",
        description: `Added ${Math.floor(entry.minutes / 60)}h ${entry.minutes % 60}m to the project`,
      });
    } catch (error) {
      console.error('Error adding time entry:', error);
      toast({
        title: "Error",
        description: "Failed to add time entry. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDeleteEntry = async (id: string) => {
    try {
      await deleteEntryMutation.mutateAsync(id);
      toast({
        title: "Time Entry Deleted",
        description: "The time entry has been removed",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete time entry. Please try again.",
        variant: "destructive"
      });
    }
  };

  return {
    entries,
    isLoadingEntries,
    handleAddEntry,
    handleDeleteEntry
  };
}